import React, { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';

import { UILoader } from '@futrli/ui-components';
import { workspaceByIdSelector } from '@futrli/shared-services';
import { useLocalisedConstantsContext } from '@futrli/localisation/core/localised-constants-context';
import { WorkspacesListingItem } from '@futrli-sdk/workspaces';

import { usePredictionsInfoContext } from '../../context/predictions-info-context';
import { StoreState } from '../../../../core/types';
import { UserGridSettingsContext } from '../../reports/user-grid-settings/user-grid-settings-context';
import { getColumnsWithFYInfo } from '../utils/forecast-columns';
import { PredictionsTotalsPageWrapper } from '../predictions-totals-page-wrapper';
import { PredictManagementContext } from '../../../../predict-management/context/predict-management-context';
import { getBalanceSheetAccountRowsWithFYInfo } from '../../reports/data-grid/format-rows';
import { getBalanceSheetConsolidatedAccountCategories } from '../util-fns/consolidation-util-fns';
import { REPORT_SECTION } from '../../../../core/constants';
import { useConsolidationDataContext } from '../../../../predict-management/context/consolidation-data-context';
import { ForecastUpdating } from '../../components/forecast-updating';

export const ConsolidationBalanceSheet = () => {
  const { workspaceId: tempWorkspaceId } = useParams();
  const workspaceId = tempWorkspaceId || '';

  const {
    consolidatedForecastReport,
    forecast,
    isLoading,
    periodType,
    totalCashPredictRow
  } = usePredictionsInfoContext();

  const intl = useIntl();

  const { financialConfig } = useContext(PredictManagementContext);

  const { nodeStaticIds } = useLocalisedConstantsContext();

  const { consolidation } = useConsolidationDataContext();

  const workspace = useSelector((state: StoreState) => workspaceByIdSelector(state.normalised, workspaceId));

  const { expandedRows, getShowZeroValueAccounts } = useContext(UserGridSettingsContext);

  const columns = useMemo(() => {
    if (!consolidatedForecastReport) {
      return [];
    }
    if (financialConfig && periodType === 'MONTHLY') {
      return getColumnsWithFYInfo(consolidatedForecastReport.data.columns, financialConfig);
    } else {
      return consolidatedForecastReport.data.columns;
    }
  }, [consolidatedForecastReport, financialConfig, periodType]);

  const dataGridRows = useMemo(() => {
    let rows = getBalanceSheetConsolidatedAccountCategories(
      intl,
      nodeStaticIds,
      workspace as WorkspacesListingItem,
      REPORT_SECTION.BalanceSheet,
      expandedRows,
      consolidatedForecastReport,
      consolidation,
      false,
      getShowZeroValueAccounts(forecast?.id || '')
    );

    if (financialConfig && periodType === 'MONTHLY' && rows) {
      rows = getBalanceSheetAccountRowsWithFYInfo(rows, columns);
    }

    return rows;
  }, [
    intl,
    nodeStaticIds,
    workspace,
    expandedRows,
    consolidatedForecastReport,
    consolidation,
    getShowZeroValueAccounts,
    forecast?.id,
    financialConfig,
    periodType,
    columns
  ]);

  if (!consolidatedForecastReport && isLoading) {
    return <UILoader isFull />;
  }

  return (
    <>
      <PredictionsTotalsPageWrapper
        columns={columns}
        totalAccountRows={dataGridRows}
        totalCashPredictRow={totalCashPredictRow}
        isBalanceSheet
      />
      <ForecastUpdating show={!!isLoading} />
    </>
  );
};
